/**
 * 🏦 Seção de Aprovação de Contas Bancárias (ADMIN) - REFATORADO
 * 
 * Padrão seguindo a tela de Tickets: botões de filtro, busca separada, StatCard
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Badge, Form, Row, Col, Spinner, Alert, Modal, InputGroup, Table } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { BankAccountService } from '../../services/api/bankAccounts';
import StatCard from '../common/StatCard';
import ApprovalFilters from './ApprovalFilters';
import { Drawer } from '../../theme/components';
import styles from './BankAccountsApprovalSection.module.scss';

const BankAccountsApprovalSection = () => {
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estado de abas
  const [activeView, setActiveView] = useState('pending');
  
  // Estados de filtros
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: ''
  });

  // Estados de Drawer e Modais
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Mapear activeView para status da API
  const getStatusFilter = () => {
    switch (activeView) {
      case 'pending':
        return 'pending';
      case 'approved':
        return 'approved';
      case 'rejected':
        return 'rejected';
      default:
        return null;
    }
  };

  useEffect(() => {
    loadData();
  }, [activeView, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const statusFilter = getStatusFilter();
      
      const filtersParams = {
        ...(statusFilter && { approval_status: statusFilter })
      };
      
      const [accountsData, statsData] = await Promise.all([
        BankAccountService.getAllBankAccounts(filtersParams),
        BankAccountService.getBankAccountsStats()
      ]);
      
      // Filtrar por data se necessário
      let filteredAccounts = accountsData.data || [];
      if (filters.date_from || filters.date_to) {
        filteredAccounts = filteredAccounts.filter(account => {
          const accountDate = new Date(account.created_at);
          if (filters.date_from) {
            const fromDate = new Date(filters.date_from);
            if (accountDate < fromDate) return false;
          }
          if (filters.date_to) {
            const toDate = new Date(filters.date_to);
            toDate.setHours(23, 59, 59, 999);
            if (accountDate > toDate) return false;
          }
          return true;
        });
      }
      
      setAccounts(filteredAccounts);
      setStats(statsData.data || null);
    } catch (err) {
      console.error('❌ Erro ao carregar dados:', err);
      setError('Erro ao carregar contas bancárias');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      date_from: '',
      date_to: ''
    });
  };

  const handleViewChange = (view) => {
    setActiveView(view);
  };

  const handleViewAccount = (account) => {
    setSelectedAccount(account);
    setShowDetailsDrawer(true);
  };

  const handleApprove = async (accountId) => {
    try {
      setActionLoading(true);
      setError('');
      
      await BankAccountService.approveBankAccount(accountId);
      setSuccess('Conta bancária aprovada com sucesso!');
      
      await loadData();
      setShowDetailsDrawer(false);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('❌ Erro ao aprovar conta:', err);
      setError(err.response?.data?.message || 'Erro ao aprovar conta');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClick = (account) => {
    setSelectedAccount(account);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      alert('Por favor, informe o motivo da rejeição');
      return;
    }
    
    try {
      setActionLoading(true);
      setError('');
      
      await BankAccountService.rejectBankAccount(selectedAccount.id, rejectionReason);
      setSuccess('Conta bancária rejeitada');
      
      setShowRejectModal(false);
      setShowDetailsDrawer(false);
      setSelectedAccount(null);
      setRejectionReason('');
      
      await loadData();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('❌ Erro ao rejeitar conta:', err);
      setError(err.response?.data?.message || 'Erro ao rejeitar conta');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Preparar estatísticas para as abas
  const tabStats = useMemo(() => {
    if (!stats) return { pending: 0, approved: 0, rejected: 0 };
    return {
      pending: stats.pending_count || 0,
      approved: stats.approved_count || 0,
      rejected: stats.rejected_count || 0
    };
  }, [stats]);

  // Filtrar accounts por busca
  const filteredAccounts = useMemo(() => {
    if (!searchTerm) return accounts;
    const term = searchTerm.toLowerCase();
    return accounts.filter(a => 
      (a.user_name && a.user_name.toLowerCase().includes(term)) ||
      (a.user_email && a.user_email.toLowerCase().includes(term)) ||
      (a.bank_name && a.bank_name.toLowerCase().includes(term)) ||
      (a.holder_name && a.holder_name.toLowerCase().includes(term)) ||
      (a.account_number && a.account_number.includes(term))
    );
  }, [accounts, searchTerm]);

  return (
    <>
      {/* Estatísticas - StatCard padronizado */}
      {stats && (
        <Row style={{ marginBottom: '24px' }}>
          <Col md={3}>
            <StatCard
              title="Total de Contas"
              value={stats.total_count || 0}
              icon="mdi:bank"
              variant="blue"
            />
          </Col>
          <Col md={3}>
            <StatCard
              title="Pendentes"
              value={stats.pending_count || 0}
              icon="mdi:clock-outline"
              variant="orange"
            />
          </Col>
          <Col md={3}>
            <StatCard
              title="Aprovadas"
              value={stats.approved_count || 0}
              icon="mdi:check-circle"
              variant="green"
            />
          </Col>
          <Col md={3}>
            <StatCard
              title="Rejeitadas"
              value={stats.rejected_count || 0}
              icon="mdi:close-circle"
              variant="purple"
            />
          </Col>
        </Row>
      )}

      {/* Card principal */}
      <Card className="bg-transparent border-0">
        <Card.Header className="d-none"></Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')} style={{ marginBottom: '24px' }}>
              <Icon icon="mdi:alert-circle" /> {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess('')} style={{ marginBottom: '24px' }}>
              <Icon icon="mdi:check-circle" /> {success}
            </Alert>
          )}

          {/* Botões de Filtro (Abas) - Padrão Tickets */}
          <div className="d-flex gap-2 flex-wrap" style={{ marginBottom: '24px', gap: '8px' }}>
            <Button
              variant={activeView === 'pending' ? 'warning' : 'outline-warning'}
              onClick={() => handleViewChange('pending')}
              className="d-flex align-items-center gap-2"
            >
              <Icon icon="mdi:clock-outline" />
              Pendentes
              {tabStats.pending > 0 && (
                <Badge bg="light" text="dark">{tabStats.pending}</Badge>
              )}
            </Button>
            
            <Button
              variant={activeView === 'approved' ? 'success' : 'outline-success'}
              onClick={() => handleViewChange('approved')}
              className="d-flex align-items-center gap-2"
            >
              <Icon icon="mdi:check-circle" />
              Aprovadas
              {tabStats.approved > 0 && (
                <Badge bg="light" text="dark">{tabStats.approved}</Badge>
              )}
            </Button>
            
            <Button
              variant={activeView === 'rejected' ? 'danger' : 'outline-danger'}
              onClick={() => handleViewChange('rejected')}
              className="d-flex align-items-center gap-2"
            >
              <Icon icon="mdi:close-circle" />
              Rejeitadas
              {tabStats.rejected > 0 && (
                <Badge bg="light" text="dark">{tabStats.rejected}</Badge>
              )}
            </Button>
          </div>

          {/* Filtros (Data) */}
          <ApprovalFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClearFilters={handleClearFilters}
            loading={loading}
          />

          {/* Busca - Separada como na tela de Tickets */}
          <div style={{ marginBottom: '24px' }}>
            <InputGroup>
              <InputGroup.Text>
                <Icon icon="mdi:magnify" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Buscar por usuário, banco, agência, conta ou titular..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>

          {/* Tabela */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-3">Carregando contas...</p>
            </div>
          ) : filteredAccounts.length === 0 ? (
            <div className="text-center py-5">
              <Icon 
                icon={activeView === 'pending' ? 'mdi:check-all' : 'mdi:bank-off'} 
                size={64}
                className="text-muted mb-3"
              />
              <h6 className="text-muted">
                {activeView === 'pending' && 'Nenhuma conta pendente encontrada'}
                {activeView === 'approved' && 'Nenhuma conta aprovada encontrada'}
                {activeView === 'rejected' && 'Nenhuma conta rejeitada encontrada'}
              </h6>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>Usuário</th>
                    <th>Responsável</th>
                    <th>Banco</th>
                    <th>Agência / Conta</th>
                    <th>Titular</th>
                    <th>PIX</th>
                    <th>Solicitado em</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.map((account) => (
                    <tr key={account.id}>
                      <td>
                        <div>
                          <div className="fw-semibold">{account.user_name}</div>
                          <small className="text-muted">{account.user_email}</small>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="fw-semibold">{account.responsible_name || '-'}</div>
                          {account.responsible_document && (
                            <small className="text-muted">{account.responsible_document}</small>
                          )}
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="fw-semibold">{account.bank_name}</div>
                          <small className="text-muted">Código: {account.bank_code}</small>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div>Ag: {account.agency}</div>
                          <div>Conta: {account.account_number}{account.account_digit ? `-${account.account_digit}` : ''}</div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="fw-semibold">{account.holder_name}</div>
                          <small className="text-muted">{account.holder_document}</small>
                        </div>
                      </td>
                      <td>
                        {account.pix_key ? (
                          <div>
                            <Badge bg="success" className="mb-1">PIX</Badge>
                            <div className="small text-truncate" style={{ maxWidth: '150px' }}>
                              {account.pix_key}
                            </div>
                            <small className="text-muted">{account.pix_type}</small>
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">{formatDate(account.created_at)}</small>
                      </td>
                      <td className="text-center">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleViewAccount(account)}
                          title="Ver detalhes"
                        >
                          <Icon icon="mdi:eye" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Drawer de Detalhes */}
      <Drawer
        show={showDetailsDrawer}
        onHide={() => {
          setShowDetailsDrawer(false);
          setSelectedAccount(null);
        }}
        title="Detalhes da Conta Bancária"
        icon="mdi:bank"
        size="lg"
        footer={
          selectedAccount && (
            <div className="d-flex gap-2 justify-content-end w-100">
              <Drawer.Button
                variant="secondary"
                onClick={() => {
                  setShowDetailsDrawer(false);
                  setSelectedAccount(null);
                }}
              >
                Fechar
              </Drawer.Button>
              
              {/* Botão Reprovar */}
              {(selectedAccount.approval_status === 'pending' || selectedAccount.approval_status === 'approved') && (
                <Drawer.Button
                  variant="danger"
                  onClick={() => {
                    setRejectionReason('');
                    setShowRejectModal(true);
                    setShowDetailsDrawer(false);
                  }}
                  icon="mdi:close-circle"
                >
                  Reprovar
                </Drawer.Button>
              )}
              
              {/* Botão Aprovar */}
              {(selectedAccount.approval_status === 'pending' || selectedAccount.approval_status === 'rejected') && (
                <Drawer.Button
                  variant="success"
                  onClick={() => handleApprove(selectedAccount.id)}
                  loading={actionLoading}
                  icon="mdi:check-circle"
                >
                  Aprovar
                </Drawer.Button>
              )}
            </div>
          )
        }
      >
        {selectedAccount ? (
          <>
            {/* Status Badge */}
            <Drawer.Section>
              <div className="d-flex justify-content-between align-items-center">
                <Drawer.Badge
                  variant={
                    selectedAccount.approval_status === 'pending' ? 'warning' :
                    selectedAccount.approval_status === 'approved' ? 'success' : 'danger'
                  }
                >
                  {selectedAccount.approval_status === 'pending' ? 'Pendente' :
                   selectedAccount.approval_status === 'approved' ? 'Aprovada' : 'Rejeitada'}
                </Drawer.Badge>
                <span style={{ fontSize: '12px', color: '#6c757d' }}>
                  Criado em {formatDate(selectedAccount.created_at)}
                </span>
              </div>
            </Drawer.Section>
            <Drawer.Divider />

            {/* Dados do Usuário */}
            <Drawer.Section title="Dados do Usuário" icon="mdi:account">
              <Drawer.Row label="Nome" value={selectedAccount.user_name} />
              <Drawer.Row label="Email" value={selectedAccount.user_email} />
            </Drawer.Section>
            <Drawer.Divider />

            {/* Dados do Responsável */}
            <Drawer.Section title="Responsável" icon="mdi:account-tie">
              <Drawer.Row label="Nome" value={selectedAccount.responsible_name || 'N/A'} />
              <Drawer.Row label="Documento" value={selectedAccount.responsible_document || 'N/A'} />
            </Drawer.Section>
            <Drawer.Divider />

            {/* Dados Bancários */}
            <Drawer.Section title="Dados Bancários" icon="mdi:bank">
              <Drawer.Row label="Banco" value={selectedAccount.bank_name} />
              <Drawer.Row label="Código do Banco" value={selectedAccount.bank_code} />
              <Drawer.Row label="Tipo de Conta" value={selectedAccount.account_type || 'N/A'} />
              <Drawer.Row label="Agência" value={selectedAccount.agency} />
              <Drawer.Row label="Conta" value={`${selectedAccount.account_number}${selectedAccount.account_digit ? `-${selectedAccount.account_digit}` : ''}`} />
            </Drawer.Section>
            <Drawer.Divider />

            {/* Dados do Titular */}
            <Drawer.Section title="Dados do Titular" icon="mdi:account-card">
              <Drawer.Row label="Nome do Titular" value={selectedAccount.holder_name} />
              <Drawer.Row label="Documento" value={selectedAccount.holder_document} />
            </Drawer.Section>

            {/* PIX */}
            {selectedAccount.pix_key && (
              <>
                <Drawer.Divider />
                <Drawer.Section title="PIX" icon="mdi:qrcode">
                  <Drawer.Row label="Chave PIX" value={selectedAccount.pix_key} />
                  <Drawer.Row label="Tipo" value={selectedAccount.pix_type || 'N/A'} />
                </Drawer.Section>
              </>
            )}

            {/* Motivo da Rejeição */}
            {selectedAccount.approval_status === 'rejected' && selectedAccount.rejection_reason && (
              <>
                <Drawer.Divider />
                <Drawer.Section title="Motivo da Rejeição" icon="mdi:alert-circle">
                  <p style={{ color: '#dc3545', fontSize: '14px' }}>{selectedAccount.rejection_reason}</p>
                </Drawer.Section>
              </>
            )}
          </>
        ) : (
          <Drawer.EmptyState 
            icon="mdi:alert"
            text="Não foi possível carregar os detalhes da conta."
          />
        )}
      </Drawer>

      {/* Modal de Rejeição */}
      <Modal
        show={showRejectModal}
        onHide={() => {
          setShowRejectModal(false);
          setSelectedAccount(null);
          setRejectionReason('');
        }}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Icon icon="mdi:alert-circle" className="me-2" />
            Rejeitar Conta Bancária
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAccount && (
            <>
              <Alert variant="warning" className="mb-3">
                <Icon icon="mdi:information" /> 
                Você está prestes a <strong>rejeitar</strong> a conta bancária de <strong>{selectedAccount.user_name}</strong>.
              </Alert>
              
              <div className="mb-3 p-3 bg-light rounded">
                <div className="row">
                  <div className="col-6">
                    <small className="text-muted">Banco:</small>
                    <div className="fw-semibold">{selectedAccount.bank_name}</div>
                  </div>
                  <div className="col-6">
                    <small className="text-muted">Agência / Conta:</small>
                    <div className="fw-semibold">
                      {selectedAccount.agency} / {selectedAccount.account_number}
                    </div>
                  </div>
                </div>
              </div>

              <Form.Group>
                <Form.Label className="fw-semibold">
                  Motivo da Rejeição *
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Descreva o motivo da rejeição (será enviado ao usuário)"
                />
                <Form.Text className="text-muted">
                  O usuário receberá este motivo por email e poderá cadastrar uma nova conta.
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowRejectModal(false);
              setSelectedAccount(null);
              setRejectionReason('');
            }}
            disabled={actionLoading}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleRejectConfirm}
            disabled={actionLoading || !rejectionReason.trim()}
          >
            {actionLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Rejeitando...
              </>
            ) : (
              <>
                <Icon icon="mdi:close-circle" className="me-1" />
                Confirmar Rejeição
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BankAccountsApprovalSection;
